import React, { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { BASE_URL } from "../config/config";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import md5 from "md5";
import { useNavigate } from "react-router-dom";

const Checkout = () => {
  const { cartItems, clearCart } = useCart(); // Added clearCart from context
  const { company } = useAuth();
  const navigate = useNavigate(); // Added for navigation
  const [showOrderLoader, setShowOrderLoader] = useState(false);

  const token = sessionStorage.getItem("authToken");
  const [formData, setFormData] = useState({
    merchantno: "",
    secretKey: "",
    callback_url: "",
    lname: "",
    fname: "",
    mobile_no: "",
    email: "",
    currency: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    description: "",
    upi_id: "",
    pay_by: "",
    payment_mode: "",
  });

  const [errors, setErrors] = useState({});
  const [selected, setSelected] = useState("upi");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generateTransactionId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `GLAMIRA-${timestamp}${randomNum}`;
  };

  const [order_id] = useState(generateTransactionId());

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0,
  );
  const shipping = 0;
  const discount = 0;
  const total = subtotal + shipping - discount;

  // Load company data when available
  useEffect(() => {
    if (company) {
      setFormData((prev) => ({
        ...prev,
        merchantno: company.merchantno || "",
        secretKey: company.secretKey || "",
        callback_url: company.callback_url || "",
        currency: company.currency || "INR",
        description:
          company.description || `Payment for ${cartItems.length} items`,
        pay_by: company.pay_by || "UPI",
        payment_mode: company.payment_mode || "upi",
      }));
    }
  }, [company, cartItems.length]);

  // Fetch location based on pincode
  useEffect(() => {
    const fetchLocation = async () => {
      if (formData.pincode && formData.pincode.length === 6) {
        try {
          const res = await axios.get(
            `https://api.postalpincode.in/pincode/${formData.pincode}`,
          );
          const data = res.data[0];

          if (data.Status === "Success") {
            const office = data.PostOffice[0];
            setFormData((prev) => ({
              ...prev,
              city: office.District || "",
              state: office.State || "",
              country: office.Country || "India",
            }));
          } else {
            toast.error("Invalid PIN code.");
          }
        } catch (error) {
          console.error("Failed to fetch address info:", error);
          toast.error("Failed to fetch address info.");
        }
      }
    };

    fetchLocation();
  }, [formData.pincode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    // Required fields validation
    const requiredFields = [
      { key: "fname", label: "First name" },
      { key: "lname", label: "Last name" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "country", label: "Country" },
    ];

    requiredFields.forEach(({ key, label }) => {
      if (!formData[key] || formData[key].trim() === "") {
        newErrors[key] = `${label} is required`;
      }
    });

    // Mobile number validation
    if (!formData.mobile_no || !/^\d{10}$/.test(formData.mobile_no)) {
      newErrors.mobile_no = "Enter valid 10-digit mobile number";
    }

    // Email validation
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    // PIN code validation
    if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Enter a valid 6-digit PIN code";
    }

    // UPI validation if selected
    if (selected === "upi" && !formData.upi_id) {
      newErrors.upi_id = "UPI ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return toast.error("Please fix the errors in the form.");
    }

    if (!token) {
      toast.error("Session expired. Please login again.");
      sessionStorage.clear();
      window.location.href = "/";
      return;
    }

    setIsSubmitting(true);
    setShowOrderLoader(true);

    try {
      // Billing Details
      const billingDetails = {
        order_id,
        upi_id: formData.upi_id,
        firstName: formData.fname,
        lastName: formData.lname,
        country: formData.country,
        streetAddress: formData.address,
        state: formData.state,
        city: formData.city,
        postcode: formData.pincode,
        phone: formData.mobile_no,
        email: formData.email,
        subtotal,
        shipping,
        discount,
        total,
        cartItems: [...cartItems],
      };

      // Save Order
      await axios.post(
        `${BASE_URL}/placeOrder`,
        { order_id, billingDetails },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const returnUrl = `https://glamiraa.in/confirm-order/${order_id}`;

      const checksumString = `${formData.merchantno}#@#${subtotal}@#@${order_id}#@#${formData.mobile_no}@#@${returnUrl}#@#${formData.secretKey}`;
      const checksum = md5(checksumString);

      const finalFormData = {
        ...formData,
        amount: subtotal,
        order_id,
        return_url: returnUrl,
        checksum,
      };

      if (!company?.payment_url) {
        throw new Error("Payment URL not configured.");
      }

      // Show loader for 2 sec
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const { data: result } = await axios.post(
        company.payment_url,
        finalFormData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (result.status === "SUCCESS" && result.payment_url) {
        clearCart();
        window.location.href = result.payment_url;
        return;
      }

      throw new Error(result.FailCause || "Payment failed.");

    } catch (error) {
      console.error("Checkout Error:", error);

      setShowOrderLoader(false);

      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showOrderLoader && (
        <div className="order-loader">
          <div className="order-loader-box">
            <div
              className="spinner-border text-dark mb-4"
              style={{ width: "70px", height: "70px" }}
            ></div>

            <h3>Processing Your Order...</h3>

            <p>
              Please wait while we confirm your order.
              <br />
              Do not refresh or close this page.
            </p>
          </div>
        </div>
      )}
      <div className="container py-5">
        <div className="row shadow bg-white rounded">
          {/* Left Side - Shipping Info */}
          <div className="col-md-6 p-4">
            <h2 className="mb-4 fw-bold">Checkout</h2>
            <h5 className="mb-3">Shipping Information</h5>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">First name</label>
                <input
                  type="text"
                  className={`form-control ${errors.fname ? "is-invalid" : ""}`}
                  name="fname"
                  value={formData.fname}
                  onChange={handleChange}
                  placeholder="Enter first name"
                />
                {errors.fname && (
                  <div className="invalid-feedback">{errors.fname}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Last name</label>
                <input
                  type="text"
                  className={`form-control ${errors.lname ? "is-invalid" : ""}`}
                  name="lname"
                  value={formData.lname}
                  onChange={handleChange}
                  placeholder="Enter last name"
                />
                {errors.lname && (
                  <div className="invalid-feedback">{errors.lname}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Phone number</label>
                <input
                  type="text"
                  className={`form-control ${errors.mobile_no ? "is-invalid" : ""}`}
                  name="mobile_no"
                  value={formData.mobile_no}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                />
                {errors.mobile_no && (
                  <div className="invalid-feedback">{errors.mobile_no}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Email address</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Country</label>
                <input
                  type="text"
                  className={`form-control ${errors.country ? "is-invalid" : ""}`}
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Enter country"
                />
                {errors.country && (
                  <div className="invalid-feedback">{errors.country}</div>
                )}
              </div>

              <div className="row mb-3">
                <div className="col">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className={`form-control ${errors.city ? "is-invalid" : ""}`}
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                  {errors.city && (
                    <div className="invalid-feedback">{errors.city}</div>
                  )}
                </div>
                <div className="col">
                  <label className="form-label">State</label>
                  <input
                    type="text"
                    className={`form-control ${errors.state ? "is-invalid" : ""}`}
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                  {errors.state && (
                    <div className="invalid-feedback">{errors.state}</div>
                  )}
                </div>
                <div className="col">
                  <label className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    className={`form-control ${errors.pincode ? "is-invalid" : ""}`}
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="6-digit PIN"
                    maxLength="6"
                  />
                  {errors.pincode && (
                    <div className="invalid-feedback">{errors.pincode}</div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Full Address</label>
                <input
                  type="text"
                  className={`form-control ${errors.address ? "is-invalid" : ""}`}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Full address"
                  required
                />
                {errors.address && (
                  <div className="invalid-feedback">{errors.address}</div>
                )}
              </div>

              <div className="form-check mb-4">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="termsCheck"
                  required
                />
                <label className="form-check-label" htmlFor="termsCheck">
                  I have read and agree to the Terms and Conditions.
                </label>
              </div>
            </form>
          </div>

          {/* Right Side - Cart Summary */}
          <div className="col-md-6 bg-light p-4 border-start">
            <h5 className="mb-4">Review your cart</h5>

            {cartItems.length === 0 ? (
              <div className="alert alert-info">
                <p className="mb-0">Your cart is empty</p>
                <p className="mb-0 small">
                  Add items to cart to proceed with checkout
                </p>
              </div>
            ) : (
              <>
                {cartItems.map((item) => (
                  <div
                    className="d-flex align-items-center mb-3"
                    key={item.product_id}
                  >
                    <img
                      src={`${BASE_URL}/uploads/${item.image}`}
                      className="rounded me-3"
                      alt={item.product_name}
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                      }}
                    />
                    <div>
                      <p className="mb-1 fw-semibold">{item.product_name}</p>
                      <small>{item.quantity}x</small>
                    </div>
                    <div
                      className="ms-auto fw-bold"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      ₹ {(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}

                <ul className="list-group mb-3">
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Subtotal</span>
                    <strong>₹ {subtotal.toFixed(2)}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Shipping</span>
                    <strong>₹ {shipping.toFixed(2)}</strong>
                  </li>
                  {discount > 0 && (
                    <li className="list-group-item d-flex justify-content-between text-success">
                      <span>Discount</span>
                      <strong>−₹ {discount.toFixed(2)}</strong>
                    </li>
                  )}
                  <li className="list-group-item d-flex justify-content-between fw-bold">
                    <span>Total</span>
                    <strong>₹ {total.toFixed(2)}</strong>
                  </li>
                </ul>

                <div className="card upi-card p-3 mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <input
                      type="radio"
                      name="paymentMethod"
                      id="upi"
                      value="upi"
                      checked={selected === "upi"}
                      onChange={() => setSelected("upi")}
                      className="form-check-input me-2"
                    />
                    <label htmlFor="upi" className="fw-semibold mb-0">
                      UPI
                    </label>
                  </div>
                  {selected === "upi" && (
                    <>
                      <input
                        type="text"
                        className={`form-control upi-input ${errors.upi_id ? "is-invalid" : ""}`}
                        name="upi_id"
                        value={formData.upi_id}
                        onChange={handleChange}
                        placeholder="Enter UPI ID (e.g. name@upi)"
                      />
                      {errors.upi_id && (
                        <div className="invalid-feedback">{errors.upi_id}</div>
                      )}
                    </>
                  )}

                  <div className="d-flex align-items-center mb-2 mt-3">
                    <input
                      type="radio"
                      name="paymentMethod"
                      id="netbanking"
                      className="form-check-input me-2"
                      disabled
                    />
                    <label
                      htmlFor="netbanking"
                      className="fw-semibold mb-0 text-secondary"
                    >
                      Netbanking (Currently Unavailable)
                    </label>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <input
                      type="radio"
                      name="paymentMethod"
                      id="card"
                      className="form-check-input me-2"
                      disabled
                    />
                    <label
                      htmlFor="card"
                      className="fw-semibold mb-0 text-secondary"
                    >
                      Card (Currently Unavailable)
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-dark text-white w-100 py-2 mb-3"
                  onClick={handleSubmit}
                  disabled={isSubmitting || cartItems.length === 0}
                >
                  {isSubmitting
                    ? "Processing..."
                    : `Pay Now - ₹ ${total.toFixed(2)}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
